"use client";

import { useEffect, useMemo, useState } from "react";
import {
  ThemeContext,
  type ThemePreference,
  type ResolvedTheme,
} from "./ThemeContext";

const STORAGE_KEY = "patharo-theme";

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function ThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [themePreference, setThemePreference] =
    useState<ThemePreference>("system");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    try {
      const stored = localStorage.getItem(STORAGE_KEY) as ThemePreference | null;

      if (stored === "light" || stored === "dark" || stored === "system") {
        setThemePreference(stored);
      } else {
        setThemePreference("system");
      }
    } catch {
      setThemePreference("system");
    }
  }, []);

  const theme = useMemo<ResolvedTheme>(() => {
    if (themePreference === "system") return getSystemTheme();
    return themePreference;
  }, [themePreference]);

  useEffect(() => {
    if (!mounted) return;

    document.documentElement.setAttribute("data-theme", theme);

    try {
      localStorage.setItem(STORAGE_KEY, themePreference);
    } catch {}
  }, [mounted, theme, themePreference]);

  useEffect(() => {
    if (!mounted) return;

    const mq = window.matchMedia("(prefers-color-scheme: dark)");

    const handler = () => {
      if (themePreference === "system") {
        document.documentElement.setAttribute(
          "data-theme",
          mq.matches ? "dark" : "light"
        );
      }
    };

    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [mounted, themePreference]);

  const toggle = () => {
    setThemePreference((prev) => {
      const current = prev === "system" ? getSystemTheme() : prev;
      return current === "dark" ? "light" : "dark";
    });
  };

  return (
    <ThemeContext.Provider
      value={{
        mounted,
        theme,
        themePreference,
        setTheme: setThemePreference,
        toggle,
        isDark: theme === "dark",
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}